// Page de connexion (/connexion, T0 §2 étapes 1 et 2, lot A) : se connecter ou créer un compte (code d'invitation),
// puis retour à l'adresse demandée (?suite=…, sinon le jeu). Le jeu renvoie vers /personnage s'il n'y a pas encore de
// personnage. Déjà connecté : « Entrer en ville » ou « Se déconnecter ». Serveur sans comptes : lien vers le jeu.

type Mode = "connexion" | "creation";
type Reponse = { statut: number; corps: Record<string, unknown> };

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;
const params = new URLSearchParams(location.search);
// Adresse de retour : un chemin de ce site seulement (jamais //autre-site ni https://…)
const brute = params.get("suite") ?? "/";
const suite = brute.startsWith("/") && !brute.startsWith("//") && !brute.startsWith("/connexion") ? brute : "/";

const champs = {
  identifiant: $<HTMLInputElement>("identifiant"),
  motDePasse: $<HTMLInputElement>("motDePasse"),
  invitation: $<HTMLInputElement>("invitation"),
};
const erreurs = {
  identifiant: $("erreurIdentifiant"),
  motDePasse: $("erreurMotDePasse"),
  invitation: $("erreurInvitation"),
  general: $("general"),
};
const valider = $<HTMLButtonElement>("valider");
let mode: Mode = "connexion";
let invitationsRequises = true;

const MESSAGES: Record<string, [keyof typeof erreurs, string]> = {
  identifiant_invalide: ["identifiant", "3 à 24 caractères : lettres, chiffres, point, tiret ou souligné."],
  identifiant_pris: ["identifiant", "Cet identifiant est déjà pris."],
  invitation_invalide: ["invitation", "Code inconnu, déjà utilisé ou expiré."],
  identifiants_invalides: ["general", "Identifiant ou mot de passe incorrect."],
  compte_suspendu: ["general", "Ce compte est suspendu. Tout va bien."],
  compte_ferme: ["general", "Ce compte est fermé."],
  origine_refusee: ["general", "Demande refusée (page ouverte depuis un autre site ?)."],
  indisponible: ["general", "Le guichet ne répond pas. Réessaie dans un moment."],
};
const MOTS_DE_PASSE: Record<string, string> = {
  trop_court: "10 caractères au moins.",
  trop_long: "200 caractères au plus.",
  courant: "Trop courant : il figure dans toutes les listes des pirates.",
  identifiant: "Trop proche de ton identifiant.",
};

function effacerErreurs() {
  for (const e of Object.values(erreurs)) e.textContent = "";
  for (const c of Object.values(champs)) c.removeAttribute("aria-invalid");
}

function montrerErreur(r: Reponse) {
  const code = String(r.corps.erreur ?? "");
  if (r.statut === 429 || code === "trop_d_essais") {
    const s = Number(r.corps.reessayer_dans) || 60;
    erreurs.general.textContent = `Trop d'essais. Réessaie dans ${s} s.`;
    return;
  }
  if (code === "mot_de_passe_faible") {
    erreurs.motDePasse.textContent = MOTS_DE_PASSE[String(r.corps.raison)] ?? "Mot de passe refusé.";
    champs.motDePasse.setAttribute("aria-invalid", "true");
    champs.motDePasse.focus();
    return;
  }
  const [ou, texte] = MESSAGES[code] ?? ["general", `Erreur inattendue (${r.statut}).`];
  erreurs[ou].textContent = texte;
  if (ou !== "general") {
    champs[ou].setAttribute("aria-invalid", "true");
    champs[ou].focus();
  }
}

async function appeler(methode: string, chemin: string, corps?: object): Promise<Reponse> {
  try {
    const r = await fetch(chemin, {
      method: methode,
      headers: corps ? { "content-type": "application/json" } : {},
      body: corps ? JSON.stringify(corps) : undefined,
      credentials: "same-origin",
      cache: "no-store",
    });
    let j: Record<string, unknown> = {};
    try {
      j = await r.json();
    } catch {}
    return { statut: r.status, corps: j };
  } catch {
    return { statut: 0, corps: { erreur: "indisponible" } };
  }
}

function choisirMode(m: Mode) {
  mode = m;
  const creation = m === "creation";
  $("ongletConnexion").setAttribute("aria-selected", String(!creation));
  $("ongletCreation").setAttribute("aria-selected", String(creation));
  $("champInvitation").hidden = !creation || !invitationsRequises;
  $("aideMotDePasse").hidden = !creation;
  $("donnees").hidden = !creation;
  champs.motDePasse.autocomplete = creation ? "new-password" : "current-password";
  valider.textContent = creation ? "Créer mon compte" : "Se connecter";
  effacerErreurs();
}

$("ongletConnexion").addEventListener("click", () => choisirMode("connexion"));
$("ongletCreation").addEventListener("click", () => choisirMode("creation"));

$<HTMLFormElement>("formulaire").addEventListener("submit", async (e) => {
  e.preventDefault();
  if (valider.disabled) return;
  effacerErreurs();
  const identifiant = champs.identifiant.value.trim();
  const motDePasse = champs.motDePasse.value;
  if (!identifiant) return montrerErreur({ statut: 400, corps: { erreur: "identifiant_invalide" } });
  if (mode === "creation" && [...motDePasse].length < 10) return montrerErreur({ statut: 400, corps: { erreur: "mot_de_passe_faible", raison: "trop_court" } });
  valider.disabled = true;
  const avant = valider.textContent;
  valider.textContent = "…";
  const r =
    mode === "creation"
      ? await appeler("POST", "/api/comptes", { identifiant, mot_de_passe: motDePasse, invitation: champs.invitation.value.trim() || undefined })
      : await appeler("POST", "/api/sessions", { identifiant, mot_de_passe: motDePasse });
  if (r.statut === 201 || r.statut === 204) {
    location.replace(suite);
    return;
  }
  valider.disabled = false;
  valider.textContent = avant;
  montrerErreur(r);
});

// --- Accès par code seul (alpha, 28/09) ---
const champCode = $<HTMLInputElement>("code");
const validerCode = $<HTMLButtonElement>("validerCode");
const CODES: Record<string, string> = {
  code_invalide: "Code inconnu ou expiré. Vérifie-le (les tirets sont facultatifs).",
  compte_suspendu: "Ce compte est suspendu. Tout va bien.",
  compte_ferme: "Ce compte est fermé.",
  origine_refusee: "Demande refusée (page ouverte depuis un autre site ?).",
  indisponible: "Le guichet ne répond pas. Réessaie dans un moment.",
};
$<HTMLFormElement>("formCode").addEventListener("submit", async (e) => {
  e.preventDefault();
  if (validerCode.disabled) return;
  const code = champCode.value.trim();
  const zone = $("erreurCode");
  zone.textContent = "";
  champCode.removeAttribute("aria-invalid");
  if (!code) return champCode.focus();
  validerCode.disabled = true;
  const r = await appeler("POST", "/api/acces", { code });
  if (r.statut === 200 || r.statut === 201) {
    location.replace(suite);
    return;
  }
  validerCode.disabled = false;
  const err = String(r.corps.erreur ?? "");
  zone.textContent =
    r.statut === 429 ? `Trop d'essais. Réessaie dans ${Number(r.corps.reessayer_dans) || 60} s.` : (CODES[err] ?? `Erreur inattendue (${r.statut}).`);
  champCode.setAttribute("aria-invalid", "true");
  champCode.focus();
});
$("versMotDePasse").addEventListener("click", () => {
  $("formCode").hidden = true;
  $("formulaires").hidden = false;
  choisirMode("connexion");
  champs.identifiant.focus();
});

$("entrer").addEventListener("click", () => location.replace(suite));
$("deconnecter").addEventListener("click", async () => {
  await appeler("DELETE", "/api/sessions");
  $("connecte").hidden = true;
  $("formCode").hidden = false;
});

// État de départ : les comptes sont-ils actifs, et suis-je déjà connecté ?
const etat = await appeler("GET", "/api/etat");
const comptes = etat.corps.comptes as { invitations?: boolean } | null | undefined;
if (etat.statut === 200 && !comptes) {
  // Serveur sans comptes (pas de base) : rien à faire ici
  $("formulaires").hidden = true;
  $("formCode").hidden = true;
  const c = $("connecte");
  c.hidden = false;
  c.querySelector("p")!.textContent = "Ce serveur tourne sans comptes : on entre directement.";
  $("deconnecter").hidden = true;
} else {
  invitationsRequises = comptes?.invitations !== false;
  const moi = await appeler("GET", "/api/moi");
  const compte = moi.corps.compte as { identifiant?: string } | undefined;
  const perso = moi.corps.personnage as { nom?: string } | null | undefined;
  if (moi.statut === 200 && compte?.identifiant) {
    $("qui").textContent = perso?.nom || compte.identifiant;
    $("connecte").hidden = false;
    $("formCode").hidden = true;
    $("formulaires").hidden = true;
  } else if (params.get("mode") === "creation" || params.get("mode") === "connexion") {
    // ancien parcours (identifiant et mot de passe), gardé pour les tests et les comptes déjà créés ainsi
    $("formCode").hidden = true;
    $("formulaires").hidden = false;
    choisirMode(params.get("mode") === "creation" ? "creation" : "connexion");
  }
}
if (!$("formCode").hidden) champCode.focus();
else champs.identifiant.focus();

export {}; // module (attente de premier niveau)
